import React from 'react';
import { Dialog, DialogContent, DialogActions, Divider, CircularProgress, Button, Typography } from '@material-ui/core';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import { Theme } from '@material-ui/core/styles/createTheme';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { ApiResponseError } from 'stores/apiStore';
import compose from 'helpers/compose';
import { inject, observer } from 'mobx-react';
import { EditAccountStore } from 'stores/editAccountStore';
import { APP_CONFIG } from 'appConfig';
import { isoDateTimeToLabel } from 'helpers/helpers';

interface Props {
  open: boolean;
  email?: string;
  phone?: string;
  isConfirmPending: boolean;
  errors?: ApiResponseError[];
  handleConfirm: () => void;
  handleClose: () => void;
  editAccountStore: EditAccountStore;
  accountId: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonIconRight: {
      marginLeft: theme.spacing(1),
    },
    buttonProgress: {
      marginLeft: theme.spacing(1),
      color: '#fff',
    },
    errorItem: {
      color: '#f00',
    },
    errorDivider: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
  }),
);

const CompleteDialog: React.FC<Props> = ({
  open,
  email,
  phone,
  isConfirmPending,
  errors,
  handleConfirm,
  handleClose,
  editAccountStore,
  accountId,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogHeader onClose={handleClose}>{t('general_send')}</DialogHeader>
      <DialogContent>
        <span
          dangerouslySetInnerHTML={{
            __html: t('dialog_complete_content', {
              email,
              phone,
              interpolation: { escapeValue: false },
            }),
          }}
        />
      </DialogContent>
      {errors && errors.length > 0 && (
        <DialogContent>
          <Divider className={classes.errorDivider} />
          <Typography variant="subtitle1">{t('dialog_complete_errors')}</Typography>
          <ul className={classes.errorItem}>
            {errors.map((error, i) => {
              const err = `globalErrors_${error.key}`;
              const translatedErr = t(err);
              return (
                <li key={`err_${i}`} className={classes.errorItem}>
                  {translatedErr && translatedErr !== err ? (
                    <>
                      <strong>{translatedErr}</strong>
                      <br />
                    </>
                  ) : (
                    <>
                      <strong>{APP_CONFIG.errorDetails ? error.message : t('general_error_title')}</strong>
                      <br />
                    </>
                  )}
                  {t('general_account')}: {accountId}
                  <br />
                  {t('general_error_id')}: {error.occurrenceId}
                  <br />
                  {t('general_error_time')}:{' '}
                  {error.timestamp && isoDateTimeToLabel(error.timestamp, 'dd. MM. yyyy HH:mm:ss')}
                </li>
              );
            })}
          </ul>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          {t('general_back')}
        </Button>

        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={isConfirmPending}>
          {t('dialog_complete_button')}
          {isConfirmPending ? (
            <CircularProgress size={20} className={classes.buttonProgress} />
          ) : (
            <ArrowForwardIos fontSize="small" className={classes.buttonIconRight} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default compose(inject('editAccountStore'), observer)(CompleteDialog);
